import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import InputMask from 'react-input-mask';
import axios from 'axios';
import config from '../../config';
import { useEntidadeContext } from '../../EntidadeContext';
import { useUserContext } from '../../UserContext';
import ImportacaoPacientesTab from './ImportacaoPacientesTab';
import ProntuarioVisualizacaoModal from './ProntuarioVisualizacaoModal';
import '../../index.css';
import { useAlertModal } from '../../AlertModalContext';

const TIPO_FICHA = 'Medico';

function normalizeString(str) {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

const isValidEmail = (email) => {
  if (!email) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const buscarEnderecoPorCep = async (cep) => {
  const response = await axios.get(`${config.apiCep}${cep}/json/`);
  return response.data;
};

// ─────────────────────────────────────────────
// COMPONENTE: Popover de agendamentos (Ideia 2)
// ─────────────────────────────────────────────
const POPOVER_HEIGHT_ESTIMATE = 320;
const POPOVER_WIDTH = 320;

const AgendamentosPopover = ({ pacienteId, children }) => {
  const [visible, setVisible]   = useState(false);
  const [dados, setDados]       = useState(null);
  const [loading, setLoading]   = useState(false);
  const [pos, setPos]           = useState({ top: 0, left: 0, openUpward: false });
  const openTimerRef            = useRef(null); // timer para ABRIR
  const closeTimerRef           = useRef(null); // timer para FECHAR
  const fetchedRef              = useRef(null);
  const wrapperRef              = useRef(null);

  const fetchDados = useCallback(async () => {
    if (fetchedRef.current === pacienteId) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/pacientes/${pacienteId}/agendamentos`);
      setDados(res.data);
      fetchedRef.current = pacienteId;
    } catch {
      setDados(null);
    } finally {
      setLoading(false);
    }
  }, [pacienteId]);

  // ── Cancela qualquer fechamento pendente e agenda abertura ──
  const handleMouseEnter = () => {
    clearTimeout(closeTimerRef.current); // cancela fechamento se estava pendente
    openTimerRef.current = setTimeout(() => {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const openUpward = spaceBelow < POPOVER_HEIGHT_ESTIMATE && spaceAbove > spaceBelow;
        const rawLeft    = rect.left + window.scrollX;
        const adjustedLeft = Math.min(rawLeft, window.innerWidth - POPOVER_WIDTH - 12);

        setPos({
          top: openUpward
            ? rect.top + window.scrollY - POPOVER_HEIGHT_ESTIMATE - 6
            : rect.bottom + window.scrollY + 6,
          left: adjustedLeft,
          openUpward,
        });
      }
      setVisible(true);
      fetchDados();
    }, 400);
  };

  // ── Cancela abertura pendente e agenda fechamento com delay ──
  const handleMouseLeave = () => {
    clearTimeout(openTimerRef.current);
    // Delay de 150ms: tempo suficiente para o mouse transitar
    // do wrapper para o popover sem fechar
    closeTimerRef.current = setTimeout(() => {
      setVisible(false);
    }, 150);
  };

  // ── Quando o mouse ENTRA no popover: cancela o fechamento ──
  const handlePopoverMouseEnter = () => {
    clearTimeout(closeTimerRef.current);
  };

  // ── Quando o mouse SAI do popover: fecha com delay curto ──
  const handlePopoverMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => {
      setVisible(false);
    }, 100);
  };

  // Limpa timers ao desmontar
  useEffect(() => {
    return () => {
      clearTimeout(openTimerRef.current);
      clearTimeout(closeTimerRef.current);
    };
  }, []);

  const fmt = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  const statusColor = (status) => {
    const map = {
      'Confirmado': { bg: '#dcfce7', color: '#16a34a' },
      'Agendado':   { bg: '#eff6ff', color: '#2563eb' },
      'Cancelado':  { bg: '#fef2f2', color: '#dc2626' },
      'Realizado':  { bg: '#f0fdf4', color: '#15803d' },
      'Faltou':     { bg: '#fef9c3', color: '#92400e' },
    };
    return map[status] || { bg: '#f3f4f6', color: '#6b7280' };
  };

  return (
    <>
      <div
        ref={wrapperRef}
        style={{ position: 'relative', display: 'inline-block' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>

      {visible && (
        <div
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
          style={{
            position:        'fixed',
            top:             pos.top,
            left:            pos.left,
            zIndex:          99999,
            width:           `${POPOVER_WIDTH}px`,
            backgroundColor: '#fff',
            borderRadius:    '12px',
            border:          '1px solid #e5e7eb',
            boxShadow:       '0 10px 40px rgba(0,0,0,0.18)',
            overflow:        'hidden',
            fontFamily:      "'Inter','Segoe UI',sans-serif",
            animation:       pos.openUpward ? 'fadeInUp 0.15s ease' : 'fadeInDown 0.15s ease',
            transform:       'translateZ(0)',
          }}
        >
          {/* Seta decorativa */}
          <div style={{
            position:        'absolute',
            ...(pos.openUpward
              ? { bottom: '-6px', borderTop: 'none', borderLeft: 'none', top: 'auto' }
              : { top: '-6px',    borderBottom: 'none', borderRight: 'none' }
            ),
            left:            '20px',
            width:           '12px',
            height:          '12px',
            backgroundColor: '#1e3a5f',
            border:          '1px solid #e5e7eb',
            transform:       'rotate(45deg)',
          }} />

          {/* Header */}
          <div style={{
            background:  'linear-gradient(135deg,#1e3a5f,#2563eb)',
            padding:     '10px 14px',
            display:     'flex',
            alignItems:  'center',
            gap:         '8px',
          }}>
            <svg width="14" height="14" fill="none" stroke="#fff" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#fff' }}>
              Agendamentos
            </span>
            {dados && (
              <span style={{
                marginLeft:      'auto',
                fontSize:        '10px',
                fontWeight:      '700',
                padding:         '1px 7px',
                borderRadius:    '999px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color:           '#fff',
              }}>
                {dados.total} total
              </span>
            )}
          </div>

          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '20px', color: '#6b7280', fontSize: '12px' }}>
              <Spinner animation="border" size="sm" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />
              Carregando...
            </div>
          ) : !dados ? (
            <div style={{ padding: '16px', textAlign: 'center', fontSize: '12px', color: '#9ca3af' }}>
              Não foi possível carregar os agendamentos.
            </div>
          ) : (
            <div style={{ padding: '12px 14px' }}>

              {/* Próximo agendamento */}
              <div style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '6px' }}>
                  📅 Próximo Agendamento
                </div>
                {dados.proximo ? (
                  <div style={{ padding: '8px 10px', borderRadius: '8px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '3px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: '#1e40af' }}>
                        {fmt(dados.proximo.data_agendamento)}
                      </span>
                      <span style={{ fontSize: '12px', color: '#3b82f6', fontWeight: '600' }}>
                        {dados.proximo.hora_marcada?.slice(0, 5)}
                      </span>
                    </div>
                    {dados.proximo.servico_nome && (
                      <div style={{ fontSize: '11px', color: '#3b82f6' }}>{dados.proximo.servico_nome}</div>
                    )}
                    {dados.proximo.funcionario_nome && (
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                        👤 {dados.proximo.funcionario_nome}
                      </div>
                    )}
                    <span style={{
                      display: 'inline-block', marginTop: '4px',
                      fontSize: '10px', fontWeight: '700',
                      padding: '1px 7px', borderRadius: '999px',
                      ...statusColor(dados.proximo.status),
                    }}>
                      {dados.proximo.status}
                    </span>
                  </div>
                ) : (
                  <div style={{ padding: '8px 10px', borderRadius: '8px', backgroundColor: '#f9fafb', border: '1px solid #f3f4f6', fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>
                    Nenhum agendamento futuro
                  </div>
                )}
              </div>

              {/* Último agendamento */}
              <div style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '6px' }}>
                  ✅ Último Agendamento
                </div>
                {dados.ultimo ? (
                  <div style={{ padding: '8px 10px', borderRadius: '8px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '3px' }}>
                      <span style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>
                        {fmt(dados.ultimo.data_agendamento)}
                      </span>
                      <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>
                        {dados.ultimo.hora_marcada?.slice(0, 5)}
                      </span>
                    </div>
                    {dados.ultimo.servico_nome && (
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>{dados.ultimo.servico_nome}</div>
                    )}
                    {dados.ultimo.funcionario_nome && (
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                        👤 {dados.ultimo.funcionario_nome}
                      </div>
                    )}
                    <span style={{
                      display: 'inline-block', marginTop: '4px',
                      fontSize: '10px', fontWeight: '700',
                      padding: '1px 7px', borderRadius: '999px',
                      ...statusColor(dados.ultimo.status),
                    }}>
                      {dados.ultimo.status}
                    </span>
                  </div>
                ) : (
                  <div style={{ padding: '8px 10px', borderRadius: '8px', backgroundColor: '#f9fafb', border: '1px solid #f3f4f6', fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>
                    Nenhum agendamento anterior
                  </div>
                )}
              </div>

              {/* Rodapé */}
              {dados.total > 0 && (
                <div style={{
                  paddingTop:   '8px',
                  borderTop:    '1px solid #f3f4f6',
                  fontSize:     '11px',
                  color:        '#9ca3af',
                  textAlign:    'center',
                }}>
                  Passe o mouse para ver • Abra o perfil para o histórico completo
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};


// ─────────────────────────────────────────────
// COMPONENTE: Aba de Agendamentos no modal
// ─────────────────────────────────────────────
const AgendamentosTab = ({ pacienteId }) => {
  const [dados, setDados]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro]   = useState('todos'); // 'todos' | 'futuros' | 'passados'
  const fetchedRef            = useRef(null);

  useEffect(() => {
    if (!pacienteId || fetchedRef.current === pacienteId) return;
    setLoading(true);
    axios.get(`/api/pacientes/${pacienteId}/agendamentos`)
      .then(res => { setDados(res.data); fetchedRef.current = pacienteId; })
      .catch(() => setDados(null))
      .finally(() => setLoading(false));
  }, [pacienteId]);

  const fmt = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR', {
      weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
    });
  };

  const statusMeta = (status) => {
    const map = {
      'Confirmado': { bg: '#dcfce7', color: '#16a34a', dot: '#22c55e' },
      'Agendado':   { bg: '#eff6ff', color: '#2563eb', dot: '#3b82f6' },
      'Cancelado':  { bg: '#fef2f2', color: '#dc2626', dot: '#ef4444' },
      'Realizado':  { bg: '#f0fdf4', color: '#15803d', dot: '#16a34a' },
      'Faltou':     { bg: '#fef9c3', color: '#92400e', dot: '#f59e0b' },
    };
    return map[status] || { bg: '#f3f4f6', color: '#6b7280', dot: '#9ca3af' };
  };

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 0', gap: '12px' }}>
      <Spinner animation="border" variant="primary" style={{ width: '28px', height: '28px' }} />
      <span style={{ fontSize: '13px', color: '#6b7280' }}>Carregando agendamentos...</span>
    </div>
  );

  if (!dados) return (
    <div style={{ textAlign: 'center', padding: '40px', fontSize: '13px', color: '#9ca3af' }}>
      Não foi possível carregar os agendamentos.
    </div>
  );

  const agendamentos = dados.agendamentos || [];
  const filtrados = filtro === 'futuros'
    ? agendamentos.filter(a => a.is_futuro)
    : filtro === 'passados'
      ? agendamentos.filter(a => !a.is_futuro)
      : agendamentos;

  const futuros  = agendamentos.filter(a => a.is_futuro).length;
  const passados = agendamentos.filter(a => !a.is_futuro).length;

  return (
    <div>
      {/* Cards de resumo */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '16px' }}>
        {[
          { label: 'Total',    value: dados.total,  bg: '#f8fafc', color: '#374151',  border: '#e5e7eb' },
          { label: 'Futuros',  value: futuros,       bg: '#eff6ff', color: '#2563eb',  border: '#bfdbfe' },
          { label: 'Passados', value: passados,      bg: '#f9fafb', color: '#6b7280',  border: '#e5e7eb' },
        ].map(card => (
          <div key={card.label} style={{
            padding: '12px', borderRadius: '10px',
            backgroundColor: card.bg, border: `1px solid ${card.border}`,
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '22px', fontWeight: '800', color: card.color }}>{card.value}</div>
            <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.4px' }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Próximo e Último em destaque */}
      {(dados.proximo || dados.ultimo) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
          {/* Próximo */}
          <div style={{ padding: '12px', borderRadius: '10px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Próximo
            </div>
            {dados.proximo ? (
              <>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#1e40af' }}>
                  {fmt(dados.proximo.data_agendamento)}
                </div>
                <div style={{ fontSize: '12px', color: '#3b82f6', marginTop: '2px' }}>
                  às {dados.proximo.hora_marcada?.slice(0, 5)}
                  {dados.proximo.servico_nome && ` • ${dados.proximo.servico_nome}`}
                </div>
              </>
            ) : (
              <div style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>Nenhum futuro</div>
            )}
          </div>

          {/* Último */}
          <div style={{ padding: '12px', borderRadius: '10px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Último
            </div>
            {dados.ultimo ? (
              <>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#374151' }}>
                  {fmt(dados.ultimo.data_agendamento)}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                  às {dados.ultimo.hora_marcada?.slice(0, 5)}
                  {dados.ultimo.servico_nome && ` • ${dados.ultimo.servico_nome}`}
                </div>
              </>
            ) : (
              <div style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>Nenhum anterior</div>
            )}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
        {[
          { key: 'todos',    label: `Todos (${dados.total})` },
          { key: 'futuros',  label: `Futuros (${futuros})` },
          { key: 'passados', label: `Passados (${passados})` },
        ].map(f => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFiltro(f.key)}
            style={{
              padding: '5px 12px', borderRadius: '999px', border: 'none',
              fontSize: '11px', fontWeight: '600', cursor: 'pointer',
              fontFamily: 'inherit',
              backgroundColor: filtro === f.key ? '#2563eb' : '#f3f4f6',
              color: filtro === f.key ? '#fff' : '#6b7280',
              transition: 'all .15s',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Timeline */}
      {filtrados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px', backgroundColor: '#f9fafb', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
          <svg width="32" height="32" fill="none" stroke="#d1d5db" viewBox="0 0 24 24" style={{ display: 'block', margin: '0 auto 8px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <div style={{ fontSize: '13px', color: '#9ca3af' }}>Nenhum agendamento encontrado</div>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          {/* Linha vertical da timeline */}
          <div style={{
            position: 'absolute', left: '15px', top: '8px',
            bottom: '8px', width: '2px',
            backgroundColor: '#e5e7eb', borderRadius: '999px',
          }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filtrados.map((ag, idx) => {
              const meta = statusMeta(ag.status);
              const isFuturo = ag.is_futuro;
              return (
                <div key={ag.id} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  {/* Dot da timeline */}
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                    backgroundColor: isFuturo ? '#eff6ff' : '#f9fafb',
                    border: `2px solid ${meta.dot}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 1,
                  }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: meta.dot }} />
                  </div>

                  {/* Conteúdo */}
                  <div style={{
                    flex: 1, padding: '10px 12px', borderRadius: '10px',
                    backgroundColor: isFuturo ? '#eff6ff' : '#f9fafb',
                    border: `1px solid ${isFuturo ? '#bfdbfe' : '#e5e7eb'}`,
                    marginBottom: idx < filtrados.length - 1 ? '0' : '0',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', color: isFuturo ? '#1e40af' : '#374151' }}>
                          {fmt(ag.data_agendamento)}
                        </span>
                        <span style={{ fontSize: '12px', color: isFuturo ? '#3b82f6' : '#6b7280', fontWeight: '600' }}>
                          {ag.hora_marcada?.slice(0, 5)}
                        </span>
                      </div>
                      <span style={{
                        fontSize: '10px', fontWeight: '700',
                        padding: '2px 8px', borderRadius: '999px',
                        backgroundColor: meta.bg, color: meta.color,
                      }}>
                        {ag.status}
                      </span>
                    </div>

                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      {ag.servico_nome && (
                        <span style={{ fontSize: '11px', color: isFuturo ? '#3b82f6' : '#6b7280', display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <svg width="10" height="10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          {ag.servico_nome}
                        </span>
                      )}
                      {ag.funcionario_nome && (
                        <span style={{ fontSize: '11px', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '3px' }}>
                          <svg width="10" height="10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          {ag.funcionario_nome}
                        </span>
                      )}
                      {isFuturo && (
                        <span style={{ fontSize: '10px', fontWeight: '700', color: '#2563eb', marginLeft: 'auto' }}>
                          📅 Futuro
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────
const PacientesTab = ({ pacientesList, onPatientCreated, onPatientUpdated, onPatientDeleted }) => {
  const [pacientes, setPacientes] = useState([]);
  const { showAlertModal } = useAlertModal();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pacienteToDelete, setPacienteToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMessage, setIsLoadingMessage] = useState('');
  const { currentUserRole } = useUserContext();
  const { currentEntidade } = useEntidadeContext();

  const nomeRef    = useRef(null);
  const cpfRef     = useRef(null);
  const emailRef   = useRef(null);
  const celularRef = useRef(null);

  const [showAlert, setShowAlert]     = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const [showProntuarioModal, setShowProntuarioModal] = useState(false);
  const [pacienteParaVisualizar, setPacienteParaVisualizar] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  const [showAnamneseModal, setShowAnamneseModal] = useState(false);
  const [anamnesePerguntas, setAnamnesePerguntas] = useState([]);
  const [anamneseRespostas, setAnamneseRespostas] = useState({});
  const [creatingAnamnese, setCreatingAnamnese]   = useState(false);
  const [existingAnamnese, setExistingAnamnese]   = useState(null);

  const [fotoPaciente, setFotoPaciente]           = useState(null);
  const [previewFotoPaciente, setPreviewFotoPaciente] = useState(null);

  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraStream, setCameraStream]       = useState(null);
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);

  const [activePage, setActivePage]   = useState(1);
  const itemsPerPage = 10;
  const [searchTerm, setSearchTerm]   = useState('');
  const [activeModalTabKey, setActiveModalTabKey] = useState('dadosPrincipais');
  const [allTags, setAllTags]         = useState([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);

  const [showModal, setShowModal]     = useState(false);
  const [modoEdicao, setModoEdicao]   = useState(false);

  const pacienteVazio = {
    id: 0, nome: '', email: '', telefone: '', cpf: '',
    data_nascimento: null, sexo: 'N', estado_civil: 'Nao informado',
    cor: 'Nao Informado', profissao: '', mae: '', pai: '',
    tp_convenio: '', op_convenio: '', nu_convenio: '', nu_sus: '',
    nm_acompanhante: '', cpf_acompanhante: '', obs: '',
    cep: '', tipoendereco: 'Residencial', estado: '', cidade: '',
    bairro: '', endereco: '', numero: '', complemento: '',
    dddcelular: '', celular: '', dddtelefone: '',
    interesses: [], foto_path: null,
  };

  const [pacienteSelecionado, setPacienteSelecionado] = useState(pacienteVazio);
  const [isLoadingAdicionais, setIsLoadingAdicionais] = useState(false);
  const [adicionaisFetchedForId, setAdicionaisFetchedForId] = useState(null);
  const [pacotesHistorico, setPacotesHistorico]       = useState([]);
  const [isLoadingHistorico, setIsLoadingHistorico]   = useState(false);
  const [historicoFetchedForId, setHistoricoFetchedForId] = useState(null);

  // ── Anamnese ──
  const handleShowAnamnese = async (paciente) => {
    setPacienteSelecionado(paciente);
    try {
      const [perguntasRes, anamneseRes] = await Promise.all([
        axios.get(`/api/panamneses?tipo_ficha=${TIPO_FICHA}`),
        axios.get(`/api/anamneses?paciente_id=${paciente.id}`),
      ]);
      setAnamnesePerguntas(perguntasRes.data);
      setExistingAnamnese(anamneseRes.data.anamneses[0]);
      setShowAnamneseModal(true);
    } catch (error) {
      console.error('Erro ao buscar perguntas da anamnese:', error);
    }
  };

  const handleAnamneseChange = (perguntaId, resposta) => {
    setAnamneseRespostas(prev => ({ ...prev, [perguntaId]: resposta }));
  };

  const handleSubmitAnamnese = async (pacienteId) => {
    setCreatingAnamnese(true);
    try {
      const respostasArray = Object.entries(anamneseRespostas).map(([perguntaId, resposta]) => ({
        pergunta_id: parseInt(perguntaId, 10), resposta,
      }));
      await axios.post(`/api/anamneses`, { paciente_id: pacienteId, tipo_ficha: 'Esteticista', respostas: respostasArray });
      setShowAnamneseModal(false);
      setAnamneseRespostas({});
    } catch (error) {
      console.error('Erro ao criar anamnese:', error);
    } finally {
      setCreatingAnamnese(false);
    }
  };

  // ── Sync props ──
  useEffect(() => { setPacientes(pacientesList); }, [pacientesList]);

  // ── Tags ──
  useEffect(() => {
    const fetchAllTags = async () => {
      if (!showModal || !currentEntidade || allTags.length > 0) return;
      setIsLoadingTags(true);
      try {
        const response = await axios.get(`/api/interesses?entidade_id=${currentEntidade.id}`);
        setAllTags((response.data.interesses || []).map(tag => ({ value: tag.id, label: tag.nome })));
      } catch {
        setAllTags([{ value: 1, label: 'Botox' }, { value: 2, label: 'Protocolo Capilar' }]);
      } finally {
        setIsLoadingTags(false);
      }
    };
    fetchAllTags();
  }, [showModal, currentEntidade, allTags.length]);

  useEffect(() => { setActivePage(1); }, [searchTerm]);

  // ── Dados adicionais lazy ──
  useEffect(() => {
    const fetchDadosAdicionais = async (pacienteId) => {
      if (!pacienteId || !currentEntidade) return;
      setIsLoadingAdicionais(true);
      try {
        const [pacienteDetailsRes, pacienteInteressesRes] = await Promise.all([
          axios.get(`/api/pacientes/${pacienteId}`),
          axios.get(`/api/pacientes/${pacienteId}/interesses`),
        ]);
        const dadosCompletos = pacienteDetailsRes.data;
        const interessesDoPaciente = pacienteInteressesRes.data.interesses || [];
        const formattedInteresses = interessesDoPaciente.map(tag => ({ value: tag.id, label: tag.nome }));
        setPacienteSelecionado(prev => ({
          ...prev, ...dadosCompletos,
          interesses: formattedInteresses,
          data_nascimento: dadosCompletos.data_nascimento ? new Date(dadosCompletos.data_nascimento) : null,
        }));
        if (dadosCompletos.foto_path) {
          setPreviewFotoPaciente(`${config.apiBaseURL}/storage/${dadosCompletos.foto_path}`);
        } else {
          setPreviewFotoPaciente(null);
        }
        setAdicionaisFetchedForId(pacienteId);
      } catch (error) {
        console.error('Erro ao buscar dados adicionais:', error);
        setPacienteSelecionado(prev => ({ ...prev, interesses: [] }));
      } finally {
        setIsLoadingAdicionais(false);
      }
    };
    if (showModal && modoEdicao && pacienteSelecionado?.id && adicionaisFetchedForId !== pacienteSelecionado.id) {
      fetchDadosAdicionais(pacienteSelecionado.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, modoEdicao, pacienteSelecionado?.id, currentEntidade, adicionaisFetchedForId]);

  // ── Pacotes lazy ──
  useEffect(() => {
    const fetchHistoricoPacotes = async (pacienteId) => {
      if (!pacienteId || !currentEntidade) return;
      setIsLoadingHistorico(true);
      try {
        const response = await axios.get(`/api/pacientes/${pacienteId}/pacotes-com-historico`);
        setPacotesHistorico(response.data.pacotes_historico || []);
        setHistoricoFetchedForId(pacienteId);
      } catch {
        setPacotesHistorico([]);
      } finally {
        setIsLoadingHistorico(false);
      }
    };
    if (showModal && activeModalTabKey === 'pacotes' && pacienteSelecionado?.id && historicoFetchedForId !== pacienteSelecionado.id) {
      fetchHistoricoPacotes(pacienteSelecionado.id);
    }
  }, [showModal, activeModalTabKey, pacienteSelecionado?.id, currentEntidade, historicoFetchedForId]);

  // ── Modal handlers ──
  const handleCloseModal = () => setShowModal(false);

  const handleShowModal = () => {
    setModoEdicao(false);
    setPacienteSelecionado(pacienteVazio);
    setFotoPaciente(null);
    setPreviewFotoPaciente(null);
    setActiveModalTabKey('dadosPrincipais');
    stopCameraStream();
    setShowModal(true);
  };

  const handleEditar = (paciente) => {
    setModoEdicao(true);
    setPacienteSelecionado({ ...paciente, interesses: [], data_nascimento: null });
    setAdicionaisFetchedForId(null);
    setHistoricoFetchedForId(null);
    setActiveModalTabKey('dadosPrincipais');
    setFotoPaciente(null);
    setPreviewFotoPaciente(null);
    stopCameraStream();
    setShowModal(true);
  };

  const handleSalvar = async () => {
    if (!currentEntidade) {
      setAlertMessage('Erro: Nenhuma entidade selecionada.');
      setShowAlert(true);
      return;
    }
    if (!pacienteSelecionado.nome || !pacienteSelecionado.dddcelular || !pacienteSelecionado.celular) {
      const camposFaltando = [];
      if (!pacienteSelecionado.nome) camposFaltando.push('Nome Completo');
      if (!pacienteSelecionado.dddcelular) camposFaltando.push('DDD Celular');
      if (!pacienteSelecionado.celular) camposFaltando.push('Celular');
      setAlertMessage(`Por favor, preencha: ${camposFaltando.join(', ')}.`);
      setShowAlert(true);
      setActiveModalTabKey('dadosPrincipais');
      if (nomeRef.current && !pacienteSelecionado.nome) nomeRef.current.classList.add('is-invalid');
      if (celularRef.current && !pacienteSelecionado.celular) celularRef.current.classList.add('is-invalid');
      return;
    }
    if (pacienteSelecionado.email && !isValidEmail(pacienteSelecionado.email)) {
      setAlertMessage('Por favor, insira um formato de email válido.');
      setShowAlert(true);
      setActiveModalTabKey('dadosPrincipais');
      if (emailRef.current) emailRef.current.classList.add('is-invalid');
      return;
    }
    if (nomeRef.current) nomeRef.current.classList.remove('is-invalid');
    if (celularRef.current) celularRef.current.classList.remove('is-invalid');

    setIsLoading(true);
    setIsLoadingMessage(modoEdicao ? 'Atualizando paciente...' : 'Criando paciente...');
    if (modoEdicao) { await handleEditPatient(); } else { await handleCreatePatient(); }
    setIsLoading(false);
  };

  const buildFormData = (data, isEdit = false) => {
    const formData = new FormData();
    formData.append('entidade_id', currentEntidade.id);
    const fields = {
      user_id: '1', nome: data.nome, cpf: data.cpf, email: data.email,
      dddcelular: data.dddcelular, celular: data.celular,
      data_nascimento: data.data_nascimento
        ? new Date(data.data_nascimento).toISOString().split('T')[0] : null,
      sexo: data.sexo, estado_civil: data.estado_civil, cor: data.cor,
      profissao: data.profissao, mae: data.mae, pai: data.pai,
      tp_convenio: data.tp_convenio, op_convenio: data.op_convenio,
      nu_convenio: data.nu_convenio, nu_sus: data.nu_sus,
      nm_acompanhante: data.nm_acompanhante, cpf_acompanhante: data.cpf_acompanhante,
      obs: data.obs, cep: data.cep, tipoendereco: data.tipoendereco,
      estado: data.estado, cidade: data.cidade, bairro: data.bairro,
      endereco: data.endereco, numero: data.numero, complemento: data.complemento,
      dddtelefone: data.dddtelefone, telefone: data.telefone, sigilo: data.sigilo,
    };
    Object.entries(fields).forEach(([k, v]) => {
      if (v !== null && v !== undefined) formData.append(k, v);
    });
    if (fotoPaciente) formData.append('foto', fotoPaciente);
    (data.interesses || []).forEach(tag => formData.append('interesses[]', tag.value));
    return formData;
  };

  const handleCreatePatient = async () => {
    try {
      const formData = buildFormData(pacienteSelecionado);
      const response = await axios.post(`/api/pacientes`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.status === 201) {
        const newPaciente = response.data.paciente;
        setPacientes(prev => [...prev, newPaciente]);
        if (onPatientCreated) onPatientCreated(newPaciente);
        handleCloseModal();
      }
    } catch (error) {
      let msg = 'Erro ao criar paciente.';
      if (error.response?.data?.errors) msg = Object.values(error.response.data.errors).flat().join('\n');
      else if (error.response?.data?.message) msg = error.response.data.message;
      msg = msg.replace(/The cpf has already been taken/gi, 'Este CPF já está cadastrado.');
      msg = msg.replace(/The email has already been taken/gi, 'Este E-mail já está cadastrado.');
      showAlertModal('Erro', msg, 'danger');
    }
  };

  const handleEditPatient = async () => {
    try {
      const formData = buildFormData(pacienteSelecionado, true);
      const response = await axios.post(`/api/pacientes/${pacienteSelecionado.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { _method: 'PUT' },
      });
      if (response.status === 200) {
        const updatedPaciente = response.data.paciente;
        setPacientes(prev => prev.map(p => p.id === pacienteSelecionado.id ? updatedPaciente : p));
        onPatientUpdated(updatedPaciente);
        handleCloseModal();
      }
    } catch (error) {
      let msg = 'Erro ao atualizar paciente.';
      if (error.response?.data?.errors) msg = Object.values(error.response.data.errors).flat().join('\n');
      else if (error.response?.data?.message) msg = error.response.data.message;
      msg = msg.replace(/The cpf has already been taken/gi, 'Este CPF já está cadastrado.');
      msg = msg.replace(/The email has already been taken/gi, 'Este E-mail já está cadastrado.');
      showAlertModal('Erro', msg, 'danger');
    }
  };

  const handleDeletar = (id) => { setPacienteToDelete(id); setShowDeleteModal(true); };

  const handleConfirmDelete = async () => {
    setIsLoading(true);
    try {
      await axios.delete(`/api/pacientes/${pacienteToDelete}/soft-delete`);
      setPacientes(prev => prev.filter(p => p.id !== pacienteToDelete));
      onPatientDeleted(pacienteToDelete);
    } catch {
      alert('Erro ao excluir paciente.');
    } finally {
      setShowDeleteModal(false);
      setPacienteToDelete(null);
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'email' && emailRef.current) emailRef.current.classList.remove('is-invalid');
    setPacienteSelecionado(prev => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    const justNumbers = e.target.value.replace(/\D/g, '');
    setPacienteSelecionado(prev => ({ ...prev, dddcelular: justNumbers.substring(0, 2), celular: justNumbers.substring(2) }));
  };

  const handleCEP = async (e) => {
    const cep = e.target.value.replace(/\D/g, '');
    if (cep.length === 8) {
      const response = await buscarEnderecoPorCep(cep);
      if (!response.erro) {
        setPacienteSelecionado(prev => ({
          ...prev, cep,
          estado: response.estado, cidade: response.localidade,
          bairro: response.bairro, endereco: response.logradouro,
          complemento: response.complemento,
        }));
      }
    }
  };

  const stopCameraStream = () => {
    if (cameraStream) { cameraStream.getTracks().forEach(t => t.stop()); setCameraStream(null); }
  };

  const handleOpenCamera = async () => {
    stopCameraStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      setCameraStream(stream);
      setShowCameraModal(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 100);
    } catch { alert('Não foi possível acessar a câmera.'); }
  };

  const handleCloseCamera = () => { stopCameraStream(); setShowCameraModal(false); };

  const handleCapturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const tw = 150, th = 200, ta = tw / th;
    canvas.width = tw; canvas.height = th;
    const va = video.videoWidth / video.videoHeight;
    let sx = 0, sy = 0, sw = video.videoWidth, sh = video.videoHeight;
    if (va > ta) { sw = video.videoHeight * ta; sx = (video.videoWidth - sw) / 2; }
    else if (va < ta) { sh = video.videoWidth / ta; sy = (video.videoHeight - sh) / 2; }
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, tw, th);
    canvas.toBlob(blob => {
      setFotoPaciente(blob);
      setPreviewFotoPaciente(URL.createObjectURL(blob));
      handleCloseCamera();
    }, 'image/jpeg', 0.9);
  };

  const handleShowProntuario = (paciente) => { setPacienteParaVisualizar(paciente); setShowProntuarioModal(true); };

  // ── Filtro e paginação ──
  const pacientesFiltrados = useMemo(() => {
    if (searchTerm.length < 3) return pacientes;
    const t = normalizeString(searchTerm);
    return pacientes.filter(p => normalizeString(p.nome).includes(t));
  }, [pacientes, searchTerm]);

  const totalPages         = Math.ceil(pacientesFiltrados.length / itemsPerPage);
  const currentPacientes   = pacientesFiltrados.slice((activePage - 1) * itemsPerPage, activePage * itemsPerPage);

  // ── ABAS DO MODAL ──
  const modalTabs = useMemo(() => {
    const base = [
      { key: 'dadosPrincipais', label: 'Dados Principais' },
      { key: 'dadosAdicionais', label: 'Dados Adicionais' },
      { key: 'pacotes',         label: 'Pacotes' },
      { key: 'agendamentos',    label: 'Agendamentos' }, // ← NOVA ABA
      { key: 'interesses',      label: 'Interesses' },
    ];
    return modoEdicao ? base : base.filter(t => t.key !== 'agendamentos' && t.key !== 'pacotes');
  }, [modoEdicao]);

  return (
    <div style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .modal-close-btn { position: absolute; top: 50%; right: 16px; transform: translateY(-50%); width: 28px; height: 28px; border-radius: 6px; border: none; background: rgba(255,255,255,0.15); color: #fff; font-size: 18px; line-height: 1; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.15s; }
        .modal-close-btn:hover { background: rgba(255,255,255,0.3); }

        
        .pacientes-table { width: 100%; border-collapse: collapse; }
        .pacientes-table thead tr { background: linear-gradient(135deg,#f8fafc,#f1f5f9); border-bottom: 2px solid #e2e8f0; }
        .pacientes-table thead th { padding: 12px 16px; font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; white-space: nowrap; }
        .pacientes-table tbody tr { border-bottom: 1px solid #f1f5f9; transition: background 0.15s; }
        .pacientes-table tbody tr:hover { background: #f8fafc; }
        .pacientes-table tbody td { padding: 10px 16px; font-size: 13px; color: #374151; vertical-align: middle; }
        .modal-tab-btn { padding: 8px 16px; border: none; background: none; font-size: 13px; font-weight: 500; color: #6b7280; border-bottom: 2px solid transparent; cursor: pointer; transition: all 0.15s; white-space: nowrap; font-family: 'Inter','Segoe UI',sans-serif; }
        .modal-tab-btn:hover { color: #374151; }
        .modal-tab-btn.active { color: #3b82f6; border-bottom-color: #3b82f6; font-weight: 600; }
        .form-field-label { font-size: 11px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 5px; }
        .form-field-input { width: 100%; padding: 8px 12px; border-radius: 8px; border: 1px solid #e5e7eb; font-size: 13px; color: #374151; font-family: 'Inter','Segoe UI',sans-serif; transition: border-color 0.15s,box-shadow 0.15s; background: #fff; }
        .form-field-input:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
        .form-field-input.is-invalid { border-color: #ef4444; }
        .section-title { font-size: 12px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.5px; margin: 20px 0 12px; padding-bottom: 8px; border-bottom: 1px solid #f3f4f6; }
        .pagination-btn { width: 32px; height: 32px; border-radius: 6px; border: 1px solid #e5e7eb; background: #fff; font-size: 12px; font-weight: 500; color: #374151; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.15s; }
        .pagination-btn:hover:not(:disabled) { background: #f3f4f6; }
        .pagination-btn.active { background: #3b82f6; color: #fff; border-color: #3b82f6; }
        .pagination-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .photo-placeholder { width: 113px; height: 151px; border-radius: 10px; background: linear-gradient(135deg,#f1f5f9,#e2e8f0); border: 2px dashed #cbd5e1; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #94a3b8; font-size: 12px; font-weight: 500; }
        .photo-preview-img { width: 113px; height: 151px; border-radius: 10px; object-fit: cover; border: 2px solid #e2e8f0; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
        .interest-badge { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 999px; background: #eff6ff; color: #1d4ed8; font-size: 11px; font-weight: 500; margin: 1px; }
        .action-btn { padding: 5px 10px; border-radius: 6px; border: none; font-size: 11px; font-weight: 600; cursor: pointer; transition: opacity 0.15s; display: inline-flex; align-items: center; gap: 4px; }
        .action-btn:hover { opacity: 0.85; }
        .pacote-progress-bar { height: 6px; border-radius: 999px; background: #f1f5f9; overflow: hidden; margin-top: 4px; }
        .pacote-progress-fill { height: 100%; border-radius: 999px; background: linear-gradient(90deg,#3b82f6,#8b5cf6); transition: width 0.3s ease; }
        .nome-hover-wrapper { cursor: default; }
        .nome-hover-wrapper:hover .nome-text { color: #2563eb; text-decoration: underline; text-decoration-style: dotted; }
      `}</style>

      {/* CABEÇALHO */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: '0 0 4px' }}>Pacientes</h2>
        <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>
          {pacientesFiltrados.length} paciente{pacientesFiltrados.length !== 1 ? 's' : ''} encontrado{pacientesFiltrados.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* BARRA DE AÇÕES */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '200px', maxWidth: '320px' }}>
          <svg style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', width: '14px', height: '14px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text" placeholder="Buscar por nome..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '8px 12px 8px 32px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151', fontFamily: 'inherit', outline: 'none' }}
            onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
            onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
          />
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={handleShowModal} style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#22c55e,#16a34a)', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(34,197,94,0.3)' }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.9'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            Novo Paciente
          </button>
          {(currentUserRole === 'Representante Legal' || currentUserRole === 'Recepção') && (
            <button onClick={() => setShowImportModal(true)} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #3b82f6', background: '#fff', color: '#3b82f6', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'inherit' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#eff6ff'; }} onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}>
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
              Importar em Lote
            </button>
          )}
        </div>
      </div>

      {/* Modal prontuário */}
      <ProntuarioVisualizacaoModal
        show={showProntuarioModal}
        onHide={() => setShowProntuarioModal(false)}
        paciente={pacienteParaVisualizar}
      />

      {/* ── TABELA ── */}
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <table className="pacientes-table">
          <thead>
            <tr>
              <th style={{ width: '52px' }}></th>
              <th>Nome</th>
              {currentUserRole !== 'Médico' && <th className="d-none d-md-table-cell">Email</th>}
              <th className="d-none d-lg-table-cell">Interesses</th>
              <th style={{ textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {currentPacientes.length === 0 ? (
              <tr>
                <td colSpan={currentUserRole !== 'Médico' ? 5 : 4} style={{ textAlign: 'center', padding: '48px 16px', color: '#9ca3af' }}>
                  <svg width="40" height="40" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ display: 'block', margin: '0 auto 12px', color: '#d1d5db' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {searchTerm.length >= 3 ? 'Nenhum paciente encontrado para esta busca.' : 'Nenhum paciente cadastrado.'}
                </td>
              </tr>
            ) : currentPacientes.map((paciente) => (
              <tr key={paciente.id}>
                {/* Avatar */}
                <td>
                  {paciente.foto_path ? (
                    <img src={`${config.apiBaseURL}/storage/${paciente.foto_path}`} alt={paciente.nome}
                      style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #e5e7eb' }} />
                  ) : (
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '700', color: '#fff' }}>
                      {paciente.nome ? paciente.nome.charAt(0).toUpperCase() : '?'}
                    </div>
                  )}
                </td>

                {/* ── Nome com Popover (Ideia 2) ── */}
                <td>
                  <AgendamentosPopover pacienteId={paciente.id}>
                    <div className="nome-hover-wrapper">
                      <div className="nome-text" style={{ fontWeight: '600', color: '#111827', fontSize: '13px', transition: 'color .15s' }}>
                        {paciente.nome}
                      </div>
                      {paciente.dddcelular && paciente.celular && (
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '1px' }}>
                          ({paciente.dddcelular}) {paciente.celular}
                        </div>
                      )}
                      {/* Dica visual sutil */}
                      <div style={{ fontSize: '10px', color: '#c7d2fe', marginTop: '1px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <svg width="9" height="9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        ver agenda
                      </div>
                    </div>
                  </AgendamentosPopover>
                </td>

                {/* Email */}
                {currentUserRole !== 'Médico' && (
                  <td className="d-none d-md-table-cell" style={{ color: '#6b7280', fontSize: '12px' }}>
                    {paciente.email || <span style={{ color: '#d1d5db', fontStyle: 'italic' }}>—</span>}
                  </td>
                )}

                {/* Interesses */}
                <td className="d-none d-lg-table-cell">
                  {paciente.interesses && paciente.interesses.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
                      {paciente.interesses.map(tag => (
                        <span key={tag.id} className="interest-badge">{tag.nome}</span>
                      ))}
                    </div>
                  ) : (
                    <span style={{ fontSize: '12px', color: '#d1d5db', fontStyle: 'italic' }}>Nenhum</span>
                  )}
                </td>

                {/* Ações */}
                <td style={{ textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    {currentUserRole !== 'Médico' && (
                      <button className="action-btn" onClick={() => handleEditar(paciente)} style={{ background: '#eff6ff', color: '#2563eb' }}>
                        <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        <span className="d-none d-md-inline">Editar</span>
                      </button>
                    )}
                    {(currentUserRole === 'Representante Legal' || currentUserRole === 'Médico') && (
                      <button className="action-btn" onClick={() => handleShowProntuario(paciente)} style={{ background: '#f0fdf4', color: '#16a34a' }}>
                        <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        <span className="d-none d-md-inline">Prontuário</span>
                      </button>
                    )}
                    {currentUserRole === 'Representante Legal' && (
                      <button className="action-btn" onClick={() => handleDeletar(paciente.id)} style={{ background: '#fef2f2', color: '#dc2626' }}>
                        <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        <span className="d-none d-md-inline">Deletar</span>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PAGINAÇÃO */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginTop: '20px' }}>
          <button className="pagination-btn" onClick={() => setActivePage(p => p - 1)} disabled={activePage === 1}>‹</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button key={page} className={`pagination-btn ${page === activePage ? 'active' : ''}`} onClick={() => setActivePage(page)}>{page}</button>
          ))}
          <button className="pagination-btn" onClick={() => setActivePage(p => p + 1)} disabled={activePage === totalPages}>›</button>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          MODAL: NOVO / EDITAR PACIENTE
      ══════════════════════════════════════════════════════════ */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header style={{ padding: '0', border: 'none' }}>
  <div style={{ position: 'relative', width: '100%', padding: '20px 24px', background: modoEdicao ? 'linear-gradient(135deg,#1e3a5f,#2563eb)' : 'linear-gradient(135deg,#064e3b,#059669)', display: 'flex', alignItems: 'center', gap: '12px' }}>
    <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg width="20" height="20" fill="none" stroke="white" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
    </div>
    <div>
      <div style={{ fontSize: '16px', fontWeight: '700', color: '#fff' }}>{modoEdicao ? 'Editar Paciente' : 'Novo Paciente'}</div>
      {modoEdicao && pacienteSelecionado.nome && (
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.75)', marginTop: '2px' }}>{pacienteSelecionado.nome}</div>
      )}
    </div>
    <button className="modal-close-btn" onClick={handleCloseModal}>×</button>
  </div>
</Modal.Header>

        <Modal.Body style={{ padding: '0', backgroundColor: '#f9fafb' }}>
          {/* Alerta */}
          {showAlert && (
            <div style={{ margin: '16px 20px 0', padding: '12px 16px', borderRadius: '8px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <svg width="16" height="16" fill="none" stroke="#ef4444" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: '1px' }}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <div style={{ flex: 1, fontSize: '13px', color: '#dc2626' }}>{alertMessage}</div>
              <button onClick={() => setShowAlert(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '16px', lineHeight: 1, padding: 0 }}>×</button>
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '24px', color: '#6b7280', fontSize: '13px' }}>
              <Spinner animation="border" size="sm" variant="primary" />
              {isLoadingMessage}
            </div>
          )}

          {/* Abas */}
          <div style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#fff', padding: '0 20px', display: 'flex', overflowX: 'auto' }}>
            {modalTabs.map(tab => (
              <button key={tab.key} className={`modal-tab-btn ${activeModalTabKey === tab.key ? 'active' : ''}`} onClick={() => setActiveModalTabKey(tab.key)}>
                {tab.label}
                {/* Badge na aba de Agendamentos */}
                {tab.key === 'agendamentos' && (
                  <span style={{ marginLeft: '5px', fontSize: '10px', fontWeight: '700', padding: '1px 5px', borderRadius: '999px', backgroundColor: activeModalTabKey === 'agendamentos' ? '#2563eb' : '#e5e7eb', color: activeModalTabKey === 'agendamentos' ? '#fff' : '#6b7280' }}>
                    📅
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Conteúdo das abas */}
          <div style={{ padding: '20px', maxHeight: '65vh', overflowY: 'auto' }}>

            {/* ABA: Dados Principais */}
            {activeModalTabKey === 'dadosPrincipais' && (
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '24px', alignItems: 'start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                  <label className="form-field-label" style={{ alignSelf: 'flex-start' }}>Foto</label>
                  {previewFotoPaciente ? (
                    <img src={previewFotoPaciente} alt="Preview" className="photo-preview-img" />
                  ) : (
                    <div className="photo-placeholder">
                      <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ marginBottom: '6px' }}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                      3x4
                    </div>
                  )}
                  <button onClick={handleOpenCamera} style={{ width: '100%', padding: '7px 10px', borderRadius: '8px', border: '1px solid #3b82f6', background: '#eff6ff', color: '#2563eb', fontSize: '11px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontFamily: 'inherit' }}>
                    <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    Capturar
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label className="form-field-label">Nome Completo *</label>
                    <input type="text" name="nome" value={pacienteSelecionado.nome} onChange={handleChange} className="form-field-input" placeholder="Nome completo do paciente" ref={nomeRef} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label className="form-field-label">CPF</label>
                      <InputMask mask="999.999.999-99" type="text" name="cpf" className="form-field-input" value={pacienteSelecionado.cpf} onChange={handleChange} placeholder="000.000.000-00" ref={cpfRef} />
                    </div>
                    <div>
                      <label className="form-field-label">Data de Nascimento</label>
                      <DatePicker selected={pacienteSelecionado.data_nascimento} onChange={date => setPacienteSelecionado(prev => ({ ...prev, data_nascimento: date }))} dateFormat="dd/MM/yyyy" className="form-field-input" placeholderText="dd/mm/aaaa" wrapperClassName="w-100" />
                    </div>
                  </div>
                  <div>
                    <label className="form-field-label">Email</label>
                    <input type="email" name="email" value={pacienteSelecionado.email} onChange={handleChange} className="form-field-input" placeholder="email@exemplo.com" ref={emailRef} />
                  </div>
                  <div>
                    <label className="form-field-label">Celular *</label>
                    <InputMask mask="(99) 99999-9999" value={`${pacienteSelecionado.dddcelular || ''}${pacienteSelecionado.celular || ''}`} onChange={handlePhoneChange}>
                      {(inputProps) => <input {...inputProps} type="tel" className="form-field-input" placeholder="(00) 00000-0000" ref={celularRef} />}
                    </InputMask>
                  </div>
                </div>
              </div>
            )}

            {/* ABA: Dados Adicionais */}
            {activeModalTabKey === 'dadosAdicionais' && (
              isLoadingAdicionais ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '40px', color: '#6b7280', fontSize: '13px' }}>
                  <Spinner animation="border" size="sm" variant="primary" /> Carregando dados adicionais...
                </div>
              ) : (
                <div>
                  <div className="section-title">Dados Pessoais</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div><label className="form-field-label">Sexo</label>
                      <select name="sexo" value={pacienteSelecionado.sexo || 'N'} onChange={handleChange} className="form-field-input">
                        <option value="N">Não Informado</option><option value="M">Masculino</option><option value="F">Feminino</option><option value="O">Outros</option>
                      </select></div>
                    <div><label className="form-field-label">Estado Civil</label>
                      <select name="estado_civil" value={pacienteSelecionado.estado_civil || 'Nao informado'} onChange={handleChange} className="form-field-input">
                        <option value="Nao informado">Selecione</option><option value="Solteiro">Solteiro(a)</option><option value="Casado">Casado(a)</option><option value="Divorciado">Divorciado(a)</option><option value="Viuvo">Viúvo(a)</option>
                      </select></div>
                    <div><label className="form-field-label">Cor/Raça</label>
                      <select name="cor" value={pacienteSelecionado.cor || 'Nao Informado'} onChange={handleChange} className="form-field-input">
                        <option value="Nao Informado">Selecione</option><option value="Branca">Branco(a)</option><option value="Negra">Negro(a)</option><option value="Parda">Pardo(a)</option><option value="Amarela">Amarelo(a)</option>
                      </select></div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div><label className="form-field-label">Profissão</label><input type="text" name="profissao" value={pacienteSelecionado.profissao || ''} onChange={handleChange} className="form-field-input" /></div>
                    <div><label className="form-field-label">Nome da Mãe</label><input type="text" name="mae" value={pacienteSelecionado.mae || ''} onChange={handleChange} className="form-field-input" /></div>
                    <div><label className="form-field-label">Nome do Pai</label><input type="text" name="pai" value={pacienteSelecionado.pai || ''} onChange={handleChange} className="form-field-input" /></div>
                  </div>
                  <div className="section-title">Convênio</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div><label className="form-field-label">Tipo</label>
                      <select name="tp_convenio" value={pacienteSelecionado.tp_convenio || ''} onChange={handleChange} className="form-field-input">
                        <option value="">Selecione</option><option value="Particular">Particular</option><option value="Convenio">Convênio Médico</option><option value="Outros">Outros</option>
                      </select></div>
                    <div><label className="form-field-label">Operadora</label><input type="text" name="op_convenio" value={pacienteSelecionado.op_convenio || ''} onChange={handleChange} className="form-field-input" /></div>
                    <div><label className="form-field-label">Nº Convênio</label><input type="number" name="nu_convenio" value={pacienteSelecionado.nu_convenio || ''} onChange={handleChange} className="form-field-input" /></div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div><label className="form-field-label">Cartão SUS</label><input type="number" name="nu_sus" value={pacienteSelecionado.nu_sus || ''} onChange={handleChange} className="form-field-input" /></div>
                    <div><label className="form-field-label">Acompanhante</label><input type="text" name="nm_acompanhante" value={pacienteSelecionado.nm_acompanhante || ''} onChange={handleChange} className="form-field-input" /></div>
                    <div><label className="form-field-label">CPF Acompanhante</label><InputMask mask="999.999.999-99" type="text" className="form-field-input" name="cpf_acompanhante" value={pacienteSelecionado.cpf_acompanhante || ''} onChange={handleChange} placeholder="000.000.000-00" /></div>
                  </div>
                  <div style={{ marginBottom: '12px' }}><label className="form-field-label">Observações</label><textarea name="obs" value={pacienteSelecionado.obs || ''} onChange={handleChange} className="form-field-input" rows={2} style={{ resize: 'vertical' }} /></div>
                  <div className="section-title">Endereço</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div><label className="form-field-label">CEP</label><InputMask mask="99.999-999" type="text" name="cep" value={pacienteSelecionado.cep || ''} onChange={handleCEP} placeholder="00.000-000" className="form-field-input" /></div>
                    <div><label className="form-field-label">Tipo de Endereço</label>
                      <select name="tipoendereco" value={pacienteSelecionado.tipoendereco || 'Residencial'} onChange={handleChange} className="form-field-input">
                        <option value="Residencial">Residencial</option><option value="Comercial">Comercial</option><option value="Cobrança">Cobrança</option><option value="Outros">Outros</option>
                      </select></div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 2fr', gap: '12px', marginBottom: '12px' }}>
                    <div><label className="form-field-label">Estado</label><input type="text" name="estado" value={pacienteSelecionado.estado || ''} onChange={handleChange} className="form-field-input" /></div>
                    <div><label className="form-field-label">Cidade</label><input type="text" name="cidade" value={pacienteSelecionado.cidade || ''} onChange={handleChange} className="form-field-input" /></div>
                    <div><label className="form-field-label">Bairro</label><input type="text" name="bairro" value={pacienteSelecionado.bairro || ''} onChange={handleChange} className="form-field-input" /></div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div><label className="form-field-label">Endereço</label><input type="text" name="endereco" value={pacienteSelecionado.endereco || ''} onChange={handleChange} className="form-field-input" /></div>
                    <div><label className="form-field-label">Número</label><input type="text" name="numero" value={pacienteSelecionado.numero || ''} onChange={handleChange} className="form-field-input" /></div>
                  </div>
                  <div style={{ marginBottom: '12px' }}><label className="form-field-label">Complemento</label><input type="text" name="complemento" value={pacienteSelecionado.complemento || ''} onChange={handleChange} className="form-field-input" /></div>
                  <div className="section-title">Contato Adicional</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div><label className="form-field-label">Celular (leitura)</label><input type="text" readOnly value={`(${pacienteSelecionado.dddcelular || ''}) ${pacienteSelecionado.celular || ''}`} className="form-field-input" style={{ background: '#f9fafb', color: '#9ca3af' }} /></div>
                    <div><label className="form-field-label">Telefone Fixo</label>
                      <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr', gap: '6px' }}>
                        <input type="text" name="dddtelefone" value={pacienteSelecionado.dddtelefone || ''} onChange={handleChange} className="form-field-input" placeholder="DDD" />
                        <input type="text" name="telefone" value={pacienteSelecionado.telefone || ''} onChange={handleChange} className="form-field-input" placeholder="Número" />
                      </div></div>
                  </div>
                  <div className="section-title" style={{ color: '#ef4444', borderBottomColor: '#fecaca' }}>🔒 Informações Sigilosas</div>
                  <div><label className="form-field-label">Descrição sigilosa</label><textarea name="sigilo" value={pacienteSelecionado.sigilo || ''} onChange={handleChange} className="form-field-input" rows={3} style={{ resize: 'vertical' }} /></div>
                </div>
              )
            )}

            {/* ABA: Pacotes */}
            {activeModalTabKey === 'pacotes' && (
              <div>
                {isLoadingHistorico ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '40px', color: '#6b7280', fontSize: '13px' }}>
                    <Spinner animation="border" size="sm" variant="primary" /> Carregando histórico de pacotes...
                  </div>
                ) : pacotesHistorico.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '48px 24px', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                    <svg width="40" height="40" fill="none" stroke="#d1d5db" viewBox="0 0 24 24" style={{ display: 'block', margin: '0 auto 12px' }}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                    <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>Nenhum pacote de sessões encontrado.</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {pacotesHistorico.map(pacote => (
                      <div key={pacote.id} style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                        <div style={{ padding: '12px 16px', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>{pacote.servico.nome}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '999px', backgroundColor: pacote.status === 'Ativo' ? '#dcfce7' : '#f3f4f6', color: pacote.status === 'Ativo' ? '#16a34a' : '#6b7280' }}>{pacote.status}</span>
                            <span style={{ fontSize: '12px', color: '#9ca3af' }}>{pacote.sessoes_utilizadas} / {pacote.sessoes_contratadas} sessões</span>
                          </div>
                        </div>
                        <div style={{ padding: '8px 16px', borderBottom: '1px solid #f3f4f6' }}>
                          <div className="pacote-progressbar"><div className="pacote-progress-fill" style={{ width: `${(pacote.sessoes_utilizadas / pacote.sessoes_contratadas) * 100}%` }} /></div>
                        </div>
                        {pacote.agendamentos_utilizados?.length > 0 ? (
                          pacote.agendamentos_utilizados.map((ag, idx, arr) => (
                            <div key={ag.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 16px', borderBottom: idx < arr.length - 1 ? '1px solid #f3f4f6' : 'none', fontSize: '12px' }}>
                              <span style={{ fontWeight: '600', color: '#374151', minWidth: '80px' }}>{new Date(ag.data_agendamento + 'T00:00:00').toLocaleDateString('pt-BR')}</span>
                              <span style={{ color: '#6b7280', minWidth: '40px' }}>{ag.hora_marcada.slice(0, 5)}</span>
                              <span style={{ padding: '1px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f3f4f6', color: '#6b7280' }}>{ag.status}</span>
                            </div>
                          ))
                        ) : (
                          <div style={{ padding: '12px 16px', fontSize: '12px', color: '#9ca3af' }}>Nenhuma sessão utilizada registrada.</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── ABA: Agendamentos (NOVA — Ideia 1) ── */}
            {activeModalTabKey === 'agendamentos' && (
              <AgendamentosTab pacienteId={pacienteSelecionado?.id} />
            )}

            {/* ABA: Interesses */}
            {activeModalTabKey === 'interesses' && (
              <div>
                <div className="section-title">Tags de Interesse</div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>Selecione as tags de interesse para este paciente:</p>
                <Select
                  isMulti options={allTags} isLoading={isLoadingTags}
                  className="basic-multi-select" classNamePrefix="select"
                  placeholder="Selecione as tags..." noOptionsMessage={() => 'Nenhuma tag cadastrada'}
                  value={pacienteSelecionado.interesses}
                  onChange={selectedOptions => setPacienteSelecionado(prev => ({ ...prev, interesses: selectedOptions || [] }))}
                  styles={{
                    control: base => ({ ...base, fontSize: '13px', borderColor: '#e5e7eb', borderRadius: '8px', minHeight: '40px' }),
                    option: base => ({ ...base, fontSize: '13px' }),
                  }}
                />
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '12px 20px', gap: '8px', backgroundColor: '#fff' }}>
          <button onClick={handleCloseModal} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', cursor: 'pointer', fontSize: '13px', fontWeight: '500', fontFamily: 'inherit' }}>Cancelar</button>
          <button onClick={handleSalvar} disabled={isLoading} style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: isLoading ? '#93c5fd' : (modoEdicao ? 'linear-gradient(135deg,#1e3a5f,#2563eb)' : 'linear-gradient(135deg,#064e3b,#059669)'), color: '#fff', cursor: isLoading ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isLoading && <Spinner as="span" animation="border" size="sm" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />}
            {isLoading ? 'Salvando...' : (modoEdicao ? 'Atualizar Paciente' : 'Criar Paciente')}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          MODAL: CONFIRMAR EXCLUSÃO
      ══════════════════════════════════════════════════════════ */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header style={{ padding: '0', border: 'none' }}>
          <div style={{ position: 'relative', width: '100%', padding: '20px 24px', background: 'linear-gradient(135deg,#7f1d1d,#dc2626)', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" fill="none" stroke="white" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#fff' }}>Confirmar Exclusão</div>
            <button className="modal-close-btn" onClick={() => setShowDeleteModal(false)}>×</button>
          </div>
        </Modal.Header>

        <Modal.Body style={{ padding: '24px', backgroundColor: '#f9fafb' }}>
          {isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '16px', color: '#6b7280', fontSize: '13px' }}>
              <Spinner animation="border" size="sm" variant="danger" /> Excluindo paciente...
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0, backgroundColor: '#fef2f2', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="18" height="18" fill="none" stroke="#ef4444" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
              </div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>Tem certeza que deseja excluir este paciente?</div>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>Esta ação não pode ser desfeita.</div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '12px 20px', gap: '8px', backgroundColor: '#fff' }}>
          <button onClick={() => setShowDeleteModal(false)} disabled={isLoading} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', cursor: isLoading ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '500', fontFamily: 'inherit' }}>Cancelar</button>
          <button onClick={handleConfirmDelete} disabled={isLoading} style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: isLoading ? '#fca5a5' : 'linear-gradient(135deg,#dc2626,#b91c1c)', color: '#fff', cursor: isLoading ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isLoading && <Spinner as="span" animation="border" size="sm" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />}
            {isLoading ? 'Excluindo...' : 'Sim, Excluir'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          MODAL: CÂMERA
      ══════════════════════════════════════════════════════════ */}
      <Modal show={showCameraModal} onHide={handleCloseCamera} centered>
        <Modal.Header style={{ padding: '0', border: 'none' }}>
          <div style={{ position: 'relative', width: '100%', padding: '16px 20px', background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="18" height="18" fill="none" stroke="white" viewBox="0 0 24 24"> strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            <span style={{ fontSize: '15px', fontWeight: '700', color: '#fff' }}>Capturar Foto</span>
            <button className="modal-close-btn" onClick={handleCloseCamera}>×</button>
          </div>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px', backgroundColor: '#f9fafb', textAlign: 'center' }}>
          {cameraStream ? (
            <video ref={videoRef} autoPlay playsInline style={{ borderRadius: '10px', maxWidth: '100%' }} />
          ) : (
            <div style={{ padding: '40px', color: '#9ca3af', fontSize: '13px' }}>
              <Spinner animation="border" size="sm" variant="primary" style={{ marginRight: '8px' }} /> Iniciando câmera...
            </div>
          )}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '12px 20px', gap: '8px', backgroundColor: '#fff' }}>
          <button onClick={handleCloseCamera} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', cursor: 'pointer', fontSize: '13px', fontWeight: '500', fontFamily: 'inherit' }}>Cancelar</button>
          <button onClick={handleCapturePhoto} disabled={!cameraStream} style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', background: !cameraStream ? '#93c5fd' : 'linear-gradient(135deg,#3b82f6,#2563eb)', color: '#fff', cursor: !cameraStream ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3" strokeWidth={2} /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /></svg>
            Capturar Foto
          </button>
        </Modal.Footer>
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          MODAL: IMPORTAÇÃO
      ══════════════════════════════════════════════════════════ */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="lg" centered>
        <Modal.Header style={{ padding: '0', border: 'none' }}>
          <div style={{ position: 'relative', width: '100%', padding: '20px 24px', background: 'linear-gradient(135deg,#4c1d95,#7c3aed)', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" fill="none" stroke="white" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
            </div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#fff' }}>Importar Pacientes via Excel</div>
            <button className="modal-close-btn" onClick={() => setShowImportModal(false)}>×</button>
          </div>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px', backgroundColor: '#f9fafb' }}>
          <ImportacaoPacientesTab onClose={() => setShowImportModal(false)} onImportComplete={onPatientCreated} />
        </Modal.Body>
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          MODAL: ANAMNESE
      ══════════════════════════════════════════════════════════ */}
      <Modal show={showAnamneseModal} onHide={() => setShowAnamneseModal(false)} size="lg" centered>
        <Modal.Header style={{ padding: '0', border: 'none' }}>
          <div style={{ position: 'relative', width: '100%', padding: '20px 24px', background: 'linear-gradient(135deg,#064e3b,#059669)', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" fill="none" stroke="white" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            </div>
            <div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#fff' }}>Ficha de Anamnese — {TIPO_FICHA}</div>
              {pacienteSelecionado.nome && <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.75)', marginTop: '2px' }}>{pacienteSelecionado.nome}</div>}
            </div>
            <button className="modal-close-btn" onClick={() => setShowAnamneseModal(false)}>×</button>
          </div>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px', backgroundColor: '#f9fafb', maxHeight: '65vh', overflowY: 'auto' }}>
          {existingAnamnese ? (
            <div>
              <div style={{ padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#16a34a', fontWeight: '500' }}>
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Anamnese já preenchida para este paciente
              </div>
              <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                {existingAnamnese.respostas.map((resposta, idx, arr) => (
                  <div key={resposta.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderBottom: idx < arr.length - 1 ? '1px solid #f3f4f6' : 'none', backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <div style={{ flex: 1, fontSize: '13px', color: '#374151' }}><strong>{resposta.pergunta.pergunta}</strong></div>
                    <span style={{ padding: '2px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '600', backgroundColor: resposta.resposta === 'Sim' ? '#dcfce7' : '#f3f4f6', color: resposta.resposta === 'Sim' ? '#16a34a' : '#6b7280', flexShrink: 0 }}>{resposta.resposta}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : anamnesePerguntas.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {anamnesePerguntas.map((pergunta, index) => (
                <div key={pergunta.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', padding: '12px 16px', borderRadius: '10px', backgroundColor: '#fff', border: '1px solid #e5e7eb' }}>
                  <span style={{ fontSize: '13px', color: '#374151', flex: 1 }}>
                    <span style={{ fontWeight: '600', color: '#9ca3af', marginRight: '8px' }}>{index + 1}.</span>
                    {pergunta.pergunta}
                  </span>
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    {['Sim', 'Não'].map(opcao => (
                      <label key={opcao} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', backgroundColor: anamneseRespostas[pergunta.id] === opcao ? (opcao === 'Sim' ? '#dcfce7' : '#fef2f2') : '#f9fafb', border: `1px solid ${anamneseRespostas[pergunta.id] === opcao ? (opcao === 'Sim' ? '#bbf7d0' : '#fecaca') : '#e5e7eb'}`, color: anamneseRespostas[pergunta.id] === opcao ? (opcao === 'Sim' ? '#16a34a' : '#dc2626') : '#6b7280' }}>
                        <input type="radio" name={`resposta-${pergunta.id}`} value={opcao} checked={anamneseRespostas[pergunta.id] === opcao} onChange={e => handleAnamneseChange(pergunta.id, e.target.value)} style={{ display: 'none' }} />
                        {opcao}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <button onClick={() => handleSubmitAnamnese(pacienteSelecionado.id)} disabled={creatingAnamnese} style={{ marginTop: '8px', padding: '10px 20px', borderRadius: '8px', border: 'none', background: creatingAnamnese ? '#86efac' : 'linear-gradient(135deg,#064e3b,#059669)', color: '#fff', cursor: creatingAnamnese ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '8px', alignSelf: 'flex-start' }}>
                {creatingAnamnese && <Spinner as="span" animation="border" size="sm" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />}
                {creatingAnamnese ? 'Salvando...' : 'Salvar Anamnese'}
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '40px', color: '#6b7280', fontSize: '13px' }}>
              <Spinner animation="border" size="sm" variant="primary" /> Carregando perguntas...
            </div>
          )}
        </Modal.Body>
      </Modal>

    </div>
  );
};

export default PacientesTab;
